import React, { memo, useCallback, useEffect, useMemo, useReducer, useRef, useState } from 'react';
import { getBackendBoolean } from '@Sage/ssm-utils/backendScalars';
import { bindActionCreators } from 'redux';
import { shallowEqual, useDispatch, useSelector } from 'react-redux';
import { SectionList } from '@Sage/ssm-web5';
import { ConfigActions, EntityExtraFieldsActions, EntityListActions } from 'actions';
import { SALESORDERSLINES } from 'constants/Entities';
import { defineBaseExtraField, groupFieldsForFieldSelector } from 'utils/fm';
import { getLiteral } from 'utils/getLiteral';
import { isSageImplementation as resolveIsSageImplementation } from 'utils/sage';
import { logEvent } from 'utils/tracking';
import ProductLinesEmptyView from '../ProductLinesEmptyView';
import { getDefaultColumnsWidth } from '../../utils';
import CONFIG from './TableConfig';

const initialState = {
    columns: [],
    columnsSizes: null,
    columnsVisibility: null,
    groupedFields: null,
};

const reducer = (state, action) => {
    switch (action.type) {
        case 'setColumnsSizes':
            return { ...state, columnsSizes: action.columnsSizes };
        case 'setColumnsVisibility':
            return { ...state, columnsVisibility: action.columnsVisibility };
        case 'setState':
            return { ...action.state };
    }
};

const SalesOrdersProductLinesTable = memo(
    ({ cacheId, data, onOrderProducts, tableParams, useDragRows = false }) => {
        // --- Redux
        const dispatch = useDispatch();

        const {
            cache,
            isSageImplementation,
            salesOrderAmountDiscounts,
            uomEnabled,
            useTaxesSalesOrders,
        } = useSelector((state) => {
            const oldCache = state?.config?.components?.[`${SALESORDERSLINES.entity}_list`] || null;
            const newCache = (cacheId && state?.config?.components?.[cacheId]) || null;

            return {
                cache: newCache || oldCache,
                isSageImplementation: resolveIsSageImplementation(state.config?.productModeCode),
                salesOrderAmountDiscounts: !!state?.config?.userData?.salesOrderAmountDiscounts,
                serverList: state.serverList || null,
                uomEnabled: !!state?.config?.userData?.uomEnabled,
                useTaxesSalesOrders: getBackendBoolean(
                    state?.config?.userData?.useTaxesSalesOrders || false,
                ),
            };
        }, shallowEqual);

        const { getExtraFieldSchema, setConfigWeb, standardFieldsConfiguration } = useMemo(
            () => ({
                getExtraFieldSchema: bindActionCreators(EntityExtraFieldsActions, dispatch)
                    .getExtraFieldSchema,
                setConfigWeb: bindActionCreators(ConfigActions, dispatch).setConfigWeb,
                standardFieldsConfiguration: bindActionCreators(EntityListActions, dispatch)
                    .standardFieldsConfiguration,
            }),
            [dispatch],
        );

        // --- State
        const tableRef = useRef();
        const [state, dispatchLocal] = useReducer(reducer, initialState);
        const [showModalFields, setShowModalFields] = useState(false);

        const handleTableRef = useCallback((ref) => {
            tableRef.current = ref;
        }, []);

        const getRowNodeId = useCallback((row) => row.id, []);

        const saveCache = useCallback(
            (newObject) => {
                const fieldsKeys = ['visibles', 'sizes'];
                const columnsConfig = fieldsKeys.reduce((obj, field) => {
                    obj[field] = newObject[field];
                    return obj;
                }, {});
                setConfigWeb(`${SALESORDERSLINES.entity}_list`, columnsConfig);
            },
            [setConfigWeb],
        );

        const getCacheConfiguration = useCallback(
            (columnsDef) => {
                const mapped = columnsDef.map((column) => {
                    const col = { ...column };
                    if (col.alwaysHidden) {
                        col.hide = true;
                    } else if (cache?.visibles?.hasOwnProperty(col.colId)) {
                        col.hide = !cache.visibles[col.colId];
                    }
                    if (cache?.sizes?.[col.colId]) col.width = cache?.sizes[col.colId];

                    return col;
                });

                if (!salesOrderAmountDiscounts) return mapped;

                const vis = cache?.visibles || {};
                return mapped.map((column) => {
                    const tierMatch = /^discount([1-4])Value$/.exec(column.colId);
                    if (!tierMatch) return column;
                    if (column.alwaysHidden) return column;
                    const pctId = `discount${tierMatch[1]}`;
                    if (vis[pctId] !== undefined && vis[column.colId] === undefined) {
                        return { ...column, hide: !vis[pctId] };
                    }
                    return column;
                });
            },
            [cache, salesOrderAmountDiscounts],
        );

        const saveVisibleColumns = useCallback(
            (newColumns) => {
                const newColumnsVisibility = {
                    ...state.columnsVisibility,
                    ...newColumns,
                };

                const newColumnsConfigObject = {
                    sizes: state.columnsSizes,
                    visibles: newColumnsVisibility,
                };

                saveCache(newColumnsConfigObject);
                dispatchLocal({
                    type: 'setColumnsVisibility',
                    columnsVisibility: newColumnsVisibility,
                });
            },
            [state.columnsSizes, state.columnsVisibility, saveCache],
        );

        const saveColumnSize = useCallback(
            (colId, size) => {
                const newColumnsSizes = { ...state.columnsSizes, [colId]: size };
                const newColumnsConfigObject = {
                    sizes: newColumnsSizes,
                    visibles: state.columnsVisibility,
                };
                saveCache(newColumnsConfigObject);
                dispatchLocal({ type: 'setColumnsSizes', columnsSizes: newColumnsSizes });
            },
            [state.columnsSizes, state.columnsVisibility, saveCache],
        );

        // --- Derived state
        const { domLayout, extraClassName } = useMemo(() => {
            // 50px each row, 400px of max-height for the table
            // we have only the header, so we have 350px for rows -> 7 rows
            if (!data?.length) {
                return {
                    domLayout: 'normal',
                    extraClassName: 'sales-order-product-lines-table__empty',
                };
            } else if (data?.length <= 7) return { domLayout: 'autoHeight', extraClassName: '' };
            else {
                return {
                    domLayout: 'normal',
                    extraClassName: 'table-with-many-rows',
                };
            }
        }, [data]);

        const fieldSelectorLinkedColumnIds = useMemo(() => {
            if (!salesOrderAmountDiscounts) return undefined;
            const tiers = isSageImplementation ? [1] : [1, 2, 3, 4];
            return tiers.reduce((acc, n) => {
                acc[`discount${n}`] = [`discount${n}Value`];
                return acc;
            }, {});
        }, [salesOrderAmountDiscounts, isSageImplementation]);

        // --- Effects

        const dataLength = data?.length ?? 0;

        useEffect(() => {
            const configParams = {
                ...(tableParams || {}),
                data: dataLength > 0 ? { length: dataLength } : undefined,
                isSageImplementation,
                salesOrderAmountDiscounts,
                uomEnabled,
                useTaxesSalesOrders,
            };
            let newConfig = CONFIG(configParams);

            getExtraFieldSchema(newConfig.entity, (extraFieldsTabs) => {
                let extraFields = [];
                const groupedExtraFields = extraFieldsTabs.map((tab) => {
                    let group = {
                        label:
                            typeof tab.descripcion !== 'string'
                                ? 'label_customized_fields'
                                : tab.descripcion,
                        fields: [],
                    };

                    group.fields = tab.tabFields.map((extraField) => {
                        const extraParams = {
                            currencyParams: { longFormat: true },
                            decimalAndIntegerParams: { emptyIfNull: true },
                            entityName: newConfig.entity.extraFieldName,
                            contextData: tableParams?.salesOrderData,
                        };
                        let newExtraField = defineBaseExtraField(extraField, extraParams);
                        extraFields.push(newExtraField);
                        return newExtraField;
                    });

                    return group;
                });

                standardFieldsConfiguration(SALESORDERSLINES, newConfig.columnDefs)
                    .then((columnDefs) => {
                        let newColumnDefs = [...columnDefs, ...extraFields];

                        newColumnDefs = getDefaultColumnsWidth(newColumnDefs);

                        newColumnDefs = getCacheConfiguration(newColumnDefs);

                        const groupedFields = [
                            ...groupFieldsForFieldSelector(newConfig, columnDefs, true),
                            ...groupedExtraFields,
                        ];

                        dispatchLocal({
                            type: 'setState',
                            state: {
                                columns: newColumnDefs,
                                columnsSizes: cache?.sizes || [],
                                columnsVisibility: cache?.visibles || [],
                                groupedFields: groupedFields,
                            },
                        });
                    })
                    .catch((error) => {
                        console.error('List configuration error:', error);
                    });
            });
        }, [
            cache,
            dataLength,
            getCacheConfiguration,
            getExtraFieldSchema,
            isSageImplementation,
            salesOrderAmountDiscounts,
            standardFieldsConfiguration,
            tableParams,
            uomEnabled,
            useTaxesSalesOrders,
        ]);

        // --- Field selector, modal, drag
        const fieldSelectorProps = useMemo(() => {
            if (!state.groupedFields) return null;
            let actions = [];

            actions.push({
                type: 'title',
                label: getLiteral('label_customize'),
            });

            actions.push({
                label: getLiteral('action_select_visible_columns'),
                icon: 'columns',
                id: 'columns',
                onClick: () => {
                    setShowModalFields(true);
                    logEvent({
                        event: SALESORDERSLINES.trueName,
                        functionality: 'visibleFields',
                        checkDuplicate: true,
                    });
                },
            });

            return {
                actions,
                groupedColumns: state.groupedFields,
                title: getLiteral('label_showHideColumns'),
            };
        }, [state.groupedFields]);

        const modalOptionsProps = useCallback(
            () => ({
                advice: getLiteral('label_visible_fields_explanation'),
                cancelText: getLiteral('action_cancel'),
                confirmText: getLiteral('action_save'),
                isOpen: showModalFields,
                onCancel: setShowModalFields,
                placeholderSearch: getLiteral('label_search_field'),
                title: getLiteral('label_visible_fields'),
            }),
            [showModalFields],
        );

        const onDragEnd = useCallback(
            (event) => {
                return new Promise((resolve, reject) => {
                    onOrderProducts(event);
                    resolve();
                });
            },
            [onOrderProducts],
        );

        const columnsRefreshOnDragEnd = useMemo(() => ['numLine', 'rowNumber'], []);

        // --- Memoized props (SectionList)
        const sectionListProps = useMemo(
            () => ({
                className: `sales-order-product-lines-table ${extraClassName}`,
                columnDefs: state.columns,
                columnsRefreshOnDragEnd,
                customEmptyViewComponent: ProductLinesEmptyView,
                domLayout,
                fieldSelector: true,
                fieldSelectorLinkedColumnIds,
                fieldSelectorProps,
                forceRowUpdating: true,
                getRowNodeId,
                id: 'sales-order-product-lines-table',
                modalOptionsProps: modalOptionsProps(),
                onDragEnd,
                onRef: handleTableRef,
                rowData: data,
                saveColumnSize,
                saveVisibleColumns,
                useDragRows,
                useSort: false,
            }),
            [
                columnsRefreshOnDragEnd,
                data,
                domLayout,
                extraClassName,
                fieldSelectorLinkedColumnIds,
                fieldSelectorProps,
                getRowNodeId,
                handleTableRef,
                modalOptionsProps,
                onDragEnd,
                saveColumnSize,
                saveVisibleColumns,
                state.columns,
                useDragRows,
            ],
        );

        // --- Render
        return <SectionList {...sectionListProps} />;
    },
);

SalesOrdersProductLinesTable.displayName = 'SalesOrdersProductLinesTable';

export default SalesOrdersProductLinesTable;
